import {
  AlignmentType,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';
import type { CalcResult, PlanInputs } from '../types';
import { fmtNum, fmtPct } from './format';

const CATEGORY_LABEL: Record<PlanInputs['category'], string> = {
  A_no: '区分A・加点希望なし',
  A_yes: '区分A・加点希望あり（年平均成長率 3.0%以上）',
  B: '区分B・既受給（年平均成長率 3.5%以上 必須）',
};

const EFFECTIVE_DESC: Record<string, string> = {
  'なし（加点希望せず）': '賃金引上げ加点を希望しません。',
  '加点取消（成長率未達）': '区分Aで加点希望ですが、成長率が必要水準を下回っています。加点は取消されますが返還義務はありません。',
  '申請要件違反（返還リスク）': '区分Bで成長率が3.5%未満です。補助金全額返還のリスクがあります。',
  '申請不可（賃金違法）': '事業場内最低賃金が地域別最低賃金を下回っており、申請できません。',
  '加点なし（賃金差不足）': '事業場内最低賃金と地域別最低賃金の差が30円未満で、賃金引上げ加点の対象外です。',
  '+30円加点取得': '+30円段階の賃金引上げ加点を取得します。',
  '+50円加点取得（最高位）': '+50円段階（最高位）の賃金引上げ加点を取得します。',
};

function p(text: string, opts?: { bold?: boolean; size?: number; align?: typeof AlignmentType.LEFT }): Paragraph {
  return new Paragraph({
    alignment: opts?.align,
    children: [new TextRun({ text, bold: opts?.bold, size: opts?.size ?? 22 })],
  });
}

function row(cells: string[], header = false): TableRow {
  return new TableRow({
    tableHeader: header,
    children: cells.map(
      (text) =>
        new TableCell({
          width: { size: 100 / cells.length, type: WidthType.PERCENTAGE },
          children: [
            new Paragraph({
              children: [new TextRun({ text, bold: header, size: 20 })],
            }),
          ],
        })
    ),
  });
}

export async function exportDocx(opts: {
  customerName: string;
  inputs: PlanInputs;
  result: CalcResult;
}): Promise<void> {
  const { customerName, inputs, result } = opts;

  const summaryRows: TableRow[] = [
    row(['項目', '値'], true),
    row(['事業者区分', CATEGORY_LABEL[inputs.category]]),
    row(['必要成長率', `${(result.requiredRate * 100).toFixed(1)}%`]),
    row(['3年度目までの実際成長率', isFinite(result.cagr3) ? `${fmtPct(result.cagr3)}%` : '—']),
    row(['3年度目の最低必要1人当給与', `${fmtNum(result.targetPerEmp3)} 円`]),
    row(['地域別最低賃金との差額', `${result.wageJudge.diff >= 0 ? '+' : ''}${result.wageJudge.diff} 円`]),
    row(['実質取得加点', result.effective]),
  ];

  const planRows: TableRow[] = [
    row(['', '前年度実績', '1年度目', '2年度目', '3年度目'], true),
    row([
      '給与支給総額（円）',
      fmtNum(inputs.prevSalary),
      fmtNum(inputs.salary1),
      fmtNum(inputs.salary2),
      fmtNum(inputs.salary3),
    ]),
    row([
      '従業員数（人）',
      String(inputs.prevEmployees),
      String(inputs.emp1),
      String(inputs.emp2),
      String(inputs.emp3),
    ]),
    row([
      '1人当たり給与（円）',
      fmtNum(result.perEmp[0]),
      fmtNum(result.perEmp[1]),
      fmtNum(result.perEmp[2]),
      fmtNum(result.perEmp[3]),
    ]),
    row([
      '年平均成長率（%）',
      '—',
      fmtPct(result.cagr1),
      fmtPct(result.cagr2),
      fmtPct(result.cagr3),
    ]),
  ];

  const systemRows: TableRow[] = [
    row(['システム入力欄', '入力値'], true),
    row(['主たる事業場の所在地', inputs.prefectureName]),
    row(['地域別最低賃金（自動表示）', `${inputs.regionMin.toLocaleString()} 円/時間`]),
    row(['事業場内最低賃金', `${inputs.siteMin.toLocaleString()} 円/時間`]),
    row(['給与支給総額（前年度実績値）', `${fmtNum(inputs.prevSalary)} 円`]),
    row(['給与支給総額（1年度目）', `${fmtNum(inputs.salary1)} 円`]),
    row(['給与支給総額（2年度目）', `${fmtNum(inputs.salary2)} 円`]),
    row(['給与支給総額（3年度目）', `${fmtNum(inputs.salary3)} 円`]),
    row(['従業員数（前年度実績値）', `${inputs.prevEmployees} 人`]),
    row(['従業員数（1年度目）', `${inputs.emp1} 人`]),
    row(['従業員数（2年度目）', `${inputs.emp2} 人`]),
    row(['従業員数（3年度目）', `${inputs.emp3} 人`]),
    row([
      '事業場内最低賃金引上げ計画の表明',
      inputs.category === 'A_no' ? 'いいえ' : 'はい',
    ]),
    row([
      '賃上げ幅',
      result.wageJudge.kind === 'plus_30'
        ? '30円以上50円未満'
        : result.wageJudge.kind === 'plus_50'
          ? '50円以上'
          : '— （加点対象外）',
    ]),
  ];

  const today = new Date().toISOString().slice(0, 10);

  const doc = new Document({
    creator: '株式会社樫乃屋',
    title: `計画数値入力_ご案内_${customerName}`,
    sections: [
      {
        properties: {},
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            heading: HeadingLevel.TITLE,
            children: [new TextRun({ text: '計画数値入力 ご案内', bold: true, size: 36 })],
          }),
          p(`お客様：${customerName}`),
          p(`作成日：${today}`),
          p(''),
          new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: '1. 計画数値サマリー', bold: true, size: 28 })] }),
          new Table({ rows: summaryRows, width: { size: 100, type: WidthType.PERCENTAGE } }),
          p(''),
          p(EFFECTIVE_DESC[result.effective] ?? ''),
          p(''),
          new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: '2. 計画数値テーブル', bold: true, size: 28 })] }),
          new Table({ rows: planRows, width: { size: 100, type: WidthType.PERCENTAGE } }),
          p(''),
          new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: '3. システム入力時の参考値', bold: true, size: 28 })] }),
          new Table({ rows: systemRows, width: { size: 100, type: WidthType.PERCENTAGE } }),
          p(''),
          new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: '4. 注意事項', bold: true, size: 28 })] }),
          p('・本書は社内検討資料です。最終判断は申請者の責任で行ってください。'),
          p('・地域別最低賃金は令和7年度改定値（参考値）です。最新の公募要領をご確認ください。'),
          p('・区分Bでは年平均成長率3.5%未満時に補助金全額返還リスクがあります。'),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `計画数値入力_ご案内_${customerName}_${today}.docx`);
}
